// Create new file: src/components/auth/ForgotPasswordForm.jsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { ArrowBack, Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const ForgotPasswordForm = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setSuccess(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error.response?.data?.error || 'An error occurred. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (success) {
    return (
      <Card sx={{ maxWidth: 450, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            height: 8, 
            width: '100%', 
            background: 'linear-gradient(90deg, #5980a6 0%, #3f6288 100%)' 
          }} 
        />
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Email sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700} color="primary">
            Check Your Email
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            If an account with that email exists, we've sent you a password reset link.
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Next steps:</strong>
            </Typography>
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Check your email inbox (and spam folder)</li>
              <li>Click the reset link in the email</li>
              <li>The link expires in 15 minutes</li>
            </ul>
          </Alert>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={handleBackToLogin}
              startIcon={<ArrowBack />}
            >
              Back to Login
            </Button>
            
            <Button
              variant="contained"
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
            >
              Send Another Email
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ maxWidth: 450, mx: 'auto', borderRadius: 2, overflow: 'hidden' }}>
      <Box 
        sx={{ 
          height: 8, 
          width: '100%', 
          background: 'linear-gradient(90deg, #5980a6 0%, #3f6288 100%)' 
        }} 
      />
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center" fontWeight={700} color="primary">
          Forgot Password?
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          No worries! Enter your email address and we'll send you a reset link.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            margin="normal"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            sx={{ mb: 3 }}
            placeholder="Enter your email address"
            autoFocus
          />
          
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            disabled={loading}
            sx={{ 
              py: 1.5,
              mb: 3,
              borderRadius: 1.5,
              boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 15px rgba(37, 99, 235, 0.35)',
              }
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Send Reset Link'
            )}
          </Button>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Remember your password?{' '}
            <Link
              component="button"
              variant="body2"
              onClick={handleBackToLogin}
              sx={{ 
                color: 'primary.main',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              Back to Login
            </Link>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ForgotPasswordForm;